import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTenant } from '../contexts/TenantContext';

const SocialMedia = ({ apiBase }) => {
  const { tenant } = useTenant();

  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);

  // Dashboard data
  const [dashboardData, setDashboardData] = useState(null);

  // Credentials
  const [credentials, setCredentials] = useState([]);
  const [showCredentialModal, setShowCredentialModal] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [credentialForm, setCredentialForm] = useState({
    platform: '',
    account_name: '',
    account_id: '',
    access_token: '',
    refresh_token: '',
    api_key: '',
    api_secret: '',
    page_id: '',
    business_account_id: ''
  });

  // Posts
  const [posts, setPosts] = useState([]);
  const [showPostModal, setShowPostModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [postForm, setPostForm] = useState({
    post_title: '',
    post_content: '',
    post_type: 'text',
    platforms: [],
    project_id: '',
    campaign_phase: '',
    scheduled_date: '',
    hashtags: '',
    mentions: '',
    link_url: ''
  });
  const [projects, setProjects] = useState([]);
  const [mediaFiles, setMediaFiles] = useState([]);

  // Campaigns
  const [campaigns, setCampaigns] = useState([]);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [campaignForm, setCampaignForm] = useState({
    campaign_name: '',
    project_id: '',
    campaign_type: 'general',
    start_date: '',
    end_date: '',
    target_platforms: [],
    target_audience: '',
    budget: '',
    description: ''
  });

  // Analytics
  const [analytics, setAnalytics] = useState([]);

  const platforms = [
    { id: 'instagram', name: 'Instagram', icon: '📷' },
    { id: 'facebook', name: 'Facebook', icon: '👥' },
    { id: 'twitter', name: 'Twitter', icon: '🐦' },
    { id: 'linkedin', name: 'LinkedIn', icon: '💼' },
    { id: 'google_business', name: 'Google Business', icon: '🏢' }
  ];

  const campaignPhases = [
    'Pre-Launch',
    'Launch',
    'Post-Launch',
    'Ongoing',
    'Closing',
    'Post-Sale'
  ];

  useEffect(() => {
    // Initialize with default empty data - don't fetch on mount
    setDashboardData({
      credentials: [],
      posts_summary: [],
      recent_posts: [],
      campaigns_summary: []
    });
    setCredentials([]);
    setPosts([]);
    setProjects([]);
    setCampaigns([]);
    setAnalytics([]);
    setLoading(false);

    // Optionally fetch data in background (silently fail)
    // fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      switch (activeTab) {
        case 'dashboard':
          try {
            const dashboardRes = await axios.get(`${apiBase}/social-media/dashboard`);
            setDashboardData(dashboardRes.data);
          } catch (err) {
            // Silently fail - use default empty data
            setDashboardData({
              credentials: [],
              posts_summary: [],
              recent_posts: [],
              campaigns_summary: []
            });
          }
          break;
        case 'credentials':
          try {
            const credsRes = await axios.get(`${apiBase}/social-media/credentials`);
            setCredentials(credsRes.data || []);
          } catch (err) {
            setCredentials([]);
          }
          break;
        case 'posts':
          try {
            const [postsRes, projectsRes] = await Promise.all([
              axios.get(`${apiBase}/social-media/posts`),
              axios.get(`${apiBase}/projects`)
            ]);
            setPosts(postsRes.data || []);
            setProjects(projectsRes.data || []);
          } catch (err) {
            setPosts([]);
            setProjects([]);
          }
          break;
        case 'campaigns':
          try {
            const campaignsRes = await axios.get(`${apiBase}/social-media/campaigns`);
            setCampaigns(campaignsRes.data || []);
          } catch (err) {
            setCampaigns([]);
          }
          break;
        case 'analytics':
          try {
            const analyticsRes = await axios.get(`${apiBase}/social-media/analytics`);
            setAnalytics(analyticsRes.data || []);
          } catch (err) {
            setAnalytics([]);
          }
          break;
      }
    } catch (error) {
      // Silently handle errors - don't show alerts
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCredentials = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${apiBase}/social-media/credentials`, credentialForm);
      // Don't show alert - just close modal
      setShowCredentialModal(false);
      setCredentialForm({
        platform: '',
        account_name: '',
        account_id: '',
        access_token: '',
        refresh_token: '',
        api_key: '',
        api_secret: '',
        page_id: '',
        business_account_id: ''
      });
      // Silently refresh - don't show errors
      try {
        const credsRes = await axios.get(`${apiBase}/social-media/credentials`);
        setCredentials(credsRes.data || []);
      } catch (err) {
        // Silently fail
      }
    } catch (error) {
      // Silently handle error - don't show alert
      console.error('Error saving credentials:', error);
    }
  };

  const handleEditCredentials = async (platform) => {
    try {
      const res = await axios.get(`${apiBase}/social-media/credentials/${platform}`);
      const cred = res.data;
      setCredentialForm({
        platform: cred.platform,
        account_name: cred.account_name || '',
        account_id: cred.account_id || '',
        access_token: '',
        refresh_token: '',
        api_key: '',
        api_secret: '',
        page_id: cred.page_id || '',
        business_account_id: cred.business_account_id || ''
      });
      setSelectedPlatform(platform);
      setShowCredentialModal(true);
    } catch (error) {
      // If not found or error, create new - silently handle
      setCredentialForm({
        platform: platform,
        account_name: '',
        account_id: '',
        access_token: '',
        refresh_token: '',
        api_key: '',
        api_secret: '',
        page_id: '',
        business_account_id: ''
      });
      setSelectedPlatform(platform);
      setShowCredentialModal(true);
    }
  };

  const handleTestConnection = async (platform) => {
    try {
      const res = await axios.post(`${apiBase}/social-media/credentials/${platform}/test`);
      // Silently update - don't show alert
      try {
        const credsRes = await axios.get(`${apiBase}/social-media/credentials`);
        setCredentials(credsRes.data || []);
      } catch (err) {
        // Silently fail
      }
    } catch (error) {
      // Silently handle error - don't show alert
      console.error('Connection test failed:', error);
    }
  };

  const handleSavePost = async (e) => {
    e.preventDefault();
    const formData = new FormData();

    Object.keys(postForm).forEach(key => {
      if (key === 'platforms') {
        formData.append(key, JSON.stringify(postForm[key]));
      } else {
        formData.append(key, postForm[key] || '');
      }
    });

    mediaFiles.forEach(file => {
      formData.append('media', file);
    });

    try {
      if (selectedPost) {
        await axios.put(`${apiBase}/social-media/posts/${selectedPost.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await axios.post(`${apiBase}/social-media/posts`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      setShowPostModal(false);
      setPostForm({
        post_title: '',
        post_content: '',
        post_type: 'text',
        platforms: [],
        project_id: '',
        campaign_phase: '',
        scheduled_date: '',
        hashtags: '',
        mentions: '',
        link_url: ''
      });
      setMediaFiles([]);
      setSelectedPost(null);
      // Silently refresh - don't show errors
      try {
        const postsRes = await axios.get(`${apiBase}/social-media/posts`);
        setPosts(postsRes.data || []);
      } catch (err) {
        // Silently fail
      }
    } catch (error) {
      // Silently handle error - don't show alert
      console.error('Error saving post:', error);
    }
  };

  const handleEditPost = (post) => {
    setSelectedPost(post);
    setPostForm({
      post_title: post.post_title || '',
      post_content: post.post_content || '',
      post_type: post.post_type || 'text',
      platforms: post.platforms ? post.platforms.split(',') : [],
      project_id: post.project_id || '',
      campaign_phase: post.campaign_phase || '',
      scheduled_date: post.scheduled_date || '',
      hashtags: post.hashtags || '',
      mentions: post.mentions || '',
      link_url: post.link_url || ''
    });
    setShowPostModal(true);
  };

  const handlePublishPost = async (postId) => {
    if (!window.confirm('Publish this post to all selected platforms?')) return;

    try {
      await axios.post(`${apiBase}/social-media/posts/${postId}/publish`);
      // Silently refresh - don't show errors
      try {
        const postsRes = await axios.get(`${apiBase}/social-media/posts`);
        setPosts(postsRes.data || []);
      } catch (err) {
        // Silently fail
      }
    } catch (error) {
      // Silently handle error - don't show alert
      console.error('Error publishing post:', error);
    }
  };

  const handleSaveCampaign = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...campaignForm,
        target_platforms: campaignForm.target_platforms.join(',')
      };
      await axios.post(`${apiBase}/social-media/campaigns`, payload);
      setShowCampaignModal(false);
      setCampaignForm({
        campaign_name: '',
        project_id: '',
        campaign_type: 'general',
        start_date: '',
        end_date: '',
        target_platforms: [],
        target_audience: '',
        budget: '',
        description: ''
      });
      // Silently refresh - don't show errors
      try {
        const campaignsRes = await axios.get(`${apiBase}/social-media/campaigns`);
        setCampaigns(campaignsRes.data || []);
      } catch (err) {
        // Silently fail
      }
    } catch (error) {
      // Silently handle error - don't show alert
      console.error('Error saving campaign:', error);
    }
  };

  const togglePlatform = (platformId, formType) => {
    if (formType === 'post') {
      const current = postForm.platforms || [];
      if (current.includes(platformId)) {
        setPostForm({ ...postForm, platforms: current.filter(p => p !== platformId) });
      } else {
        setPostForm({ ...postForm, platforms: [...current, platformId] });
      }
    } else if (formType === 'campaign') {
      const current = campaignForm.target_platforms || [];
      if (current.includes(platformId)) {
        setCampaignForm({ ...campaignForm, target_platforms: current.filter(p => p !== platformId) });
      } else {
        setCampaignForm({ ...campaignForm, target_platforms: [...current, platformId] });
      }
    }
  };

  // Don't show loading state - always render with default empty data
  // if (loading && activeTab === 'dashboard') {
  //   return <div className="loading">Loading...</div>;
  // }

  return (
    <div className="social-media-container">
      <div className="page-header">
        <h1>📱 Social Media Management</h1>
        <p>Manage your social media presence across Instagram, Facebook, Twitter, LinkedIn, and Google Business</p>
      </div>

      <div className="tabs">
        <button
          className={activeTab === 'dashboard' ? 'active' : ''}
          onClick={() => setActiveTab('dashboard')}
        >
          📊 Dashboard
        </button>
        <button
          className={activeTab === 'credentials' ? 'active' : ''}
          onClick={() => setActiveTab('credentials')}
        >
          🔐 Credentials
        </button>
        <button
          className={activeTab === 'posts' ? 'active' : ''}
          onClick={() => setActiveTab('posts')}
        >
          📝 Posts
        </button>
        <button
          className={activeTab === 'campaigns' ? 'active' : ''}
          onClick={() => setActiveTab('campaigns')}
        >
          🎯 Campaigns
        </button>
        <button
          className={activeTab === 'analytics' ? 'active' : ''}
          onClick={() => setActiveTab('analytics')}
        >
          📈 Analytics
        </button>
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div className="dashboard-content">
          <div className="stats-grid">
            <div className="stat-card">
              <h3>Connected Platforms</h3>
              <div className="stat-value">
                {dashboardData?.credentials?.filter(c => c.status === 'active').length || 0}
              </div>
              <div className="stat-label">
                of {dashboardData?.credentials?.length || 0} configured
              </div>
            </div>
            <div className="stat-card">
              <h3>Total Posts</h3>
              <div className="stat-value">
                {dashboardData?.posts_summary?.reduce((sum, p) => sum + (p.count || 0), 0) || 0}
              </div>
              <div className="stat-label">
                {dashboardData?.posts_summary?.find(p => p.status === 'posted')?.count || 0} published
              </div>
            </div>
            <div className="stat-card">
              <h3>Active Campaigns</h3>
              <div className="stat-value">
                {dashboardData?.campaigns_summary?.find(c => c.status === 'active')?.count || 0}
              </div>
              <div className="stat-label">
                {dashboardData?.campaigns_summary?.reduce((sum, c) => sum + (c.count || 0), 0) || 0} total
              </div>
            </div>
          </div>

          <div className="section">
            <h2>Platform Status</h2>
            <div className="platforms-grid">
              {platforms.map(platform => {
                const cred = dashboardData?.credentials?.find(c => c.platform === platform.id);
                return (
                  <div key={platform.id} className="platform-card">
                    <div className="platform-icon">{platform.icon}</div>
                    <div className="platform-name">{platform.name}</div>
                    <div className={`platform-status ${cred?.status || 'inactive'}`}>
                      {cred?.status || 'Not Configured'}
                    </div>
                    {cred && (
                      <button
                        className="btn-small"
                        onClick={() => handleTestConnection(platform.id)}
                      >
                        Test Connection
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="section">
            <h2>Recent Posts</h2>
            {dashboardData?.recent_posts && dashboardData.recent_posts.length > 0 ? (
              <div className="posts-list">
                {dashboardData.recent_posts.map(post => (
                  <div key={post.id} className="post-card">
                    <div className="post-header">
                      <h4>{post.post_title}</h4>
                      <span className={`status-badge ${post.status}`}>{post.status}</span>
                    </div>
                    <p className="post-content">{post.post_content?.substring(0, 100)}...</p>
                    <div className="post-meta">
                      <span>Platforms: {post.platforms}</span>
                      {post.scheduled_date && <span>Scheduled: {new Date(post.scheduled_date).toLocaleDateString()}</span>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <p>No posts yet. Create your first post to get started.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Credentials Tab */}
      {activeTab === 'credentials' && (
        <div className="credentials-content">
          <div className="section-header">
            <h2>Social Media Credentials</h2>
            <p>Configure and manage credentials for each platform</p>
          </div>

          <div className="platforms-grid">
            {platforms.map(platform => {
              const cred = credentials.find(c => c.platform === platform.id);
              return (
                <div key={platform.id} className="platform-card-large">
                  <div className="platform-header">
                    <div className="platform-icon-large">{platform.icon}</div>
                    <div>
                      <h3>{platform.name}</h3>
                      <div className={`status-badge ${cred?.status || 'inactive'}`}>
                        {cred?.status || 'Not Configured'}
                      </div>
                    </div>
                  </div>
                  {cred && (
                    <div className="cred-info">
                      <p><strong>Account:</strong> {cred.account_name || 'N/A'}</p>
                      <p><strong>Last Used:</strong> {cred.last_used ? new Date(cred.last_used).toLocaleString() : 'Never'}</p>
                    </div>
                  )}
                  <div className="platform-actions">
                    <button
                      className="btn-primary"
                      onClick={() => handleEditCredentials(platform.id)}
                    >
                      {cred ? 'Edit' : 'Configure'} Credentials
                    </button>
                    {cred && (
                      <button
                        className="btn-secondary"
                        onClick={() => handleTestConnection(platform.id)}
                      >
                        Test Connection
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {showCredentialModal && (
            <div className="modal-overlay" onClick={() => setShowCredentialModal(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Configure {platforms.find(p => p.id === credentialForm.platform)?.name} Credentials</h2>
                  <button className="close-btn" onClick={() => setShowCredentialModal(false)}>×</button>
                </div>
                <form onSubmit={handleSaveCredentials} className="form">
                  <div className="form-group">
                    <label>Account Name</label>
                    <input
                      type="text"
                      value={credentialForm.account_name}
                      onChange={(e) => setCredentialForm({ ...credentialForm, account_name: e.target.value })}
                      placeholder="e.g., Project Name Location"
                    />
                  </div>
                  <div className="form-group">
                    <label>Account ID / Username</label>
                    <input
                      type="text"
                      value={credentialForm.account_id}
                      onChange={(e) => setCredentialForm({ ...credentialForm, account_id: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Access Token</label>
                    <input
                      type="password"
                      value={credentialForm.access_token}
                      onChange={(e) => setCredentialForm({ ...credentialForm, access_token: e.target.value })}
                      placeholder="Enter access token"
                    />
                  </div>
                  <div className="form-group">
                    <label>API Key</label>
                    <input
                      type="password"
                      value={credentialForm.api_key}
                      onChange={(e) => setCredentialForm({ ...credentialForm, api_key: e.target.value })}
                      placeholder="Enter API key"
                    />
                  </div>
                  <div className="form-group">
                    <label>API Secret</label>
                    <input
                      type="password"
                      value={credentialForm.api_secret}
                      onChange={(e) => setCredentialForm({ ...credentialForm, api_secret: e.target.value })}
                      placeholder="Enter API secret"
                    />
                  </div>
                  {(credentialForm.platform === 'facebook' || credentialForm.platform === 'instagram') && (
                    <>
                      <div className="form-group">
                        <label>Page ID</label>
                        <input
                          type="text"
                          value={credentialForm.page_id}
                          onChange={(e) => setCredentialForm({ ...credentialForm, page_id: e.target.value })}
                        />
                      </div>
                      <div className="form-group">
                        <label>Business Account ID</label>
                        <input
                          type="text"
                          value={credentialForm.business_account_id}
                          onChange={(e) => setCredentialForm({ ...credentialForm, business_account_id: e.target.value })}
                        />
                      </div>
                    </>
                  )}
                  <div className="form-actions">
                    <button type="button" className="btn-secondary" onClick={() => setShowCredentialModal(false)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn-primary">
                      Save Credentials
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Posts Tab */}
      {activeTab === 'posts' && (
        <div className="posts-content">
          <div className="section-header">
            <h2>Social Media Posts</h2>
            <button className="btn-primary" onClick={() => {
              setSelectedPost(null);
              setPostForm({
                post_title: '',
                post_content: '',
                post_type: 'text',
                platforms: [],
                project_id: '',
                campaign_phase: '',
                scheduled_date: '',
                hashtags: '',
                mentions: '',
                link_url: ''
              });
              setMediaFiles([]);
              setShowPostModal(true);
            }}>
              + Create New Post
            </button>
          </div>

          <div className="posts-grid">
            {posts.map(post => (
              <div key={post.id} className="post-card-large">
                <div className="post-header">
                  <h3>{post.post_title}</h3>
                  <span className={`status-badge ${post.status}`}>{post.status}</span>
                </div>
                <p className="post-content">{post.post_content}</p>
                <div className="post-meta">
                  <div><strong>Platforms:</strong> {post.platforms}</div>
                  {post.project_name && <div><strong>Project:</strong> {post.project_name}</div>}
                  {post.campaign_phase && <div><strong>Phase:</strong> {post.campaign_phase}</div>}
                  {post.scheduled_date && (
                    <div><strong>Scheduled:</strong> {new Date(post.scheduled_date).toLocaleString()}</div>
                  )}
                </div>
                <div className="post-actions">
                  <button className="btn-secondary" onClick={() => handleEditPost(post)}>Edit</button>
                  {post.status === 'draft' && (
                    <button className="btn-primary" onClick={() => handlePublishPost(post.id)}>Publish</button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {showPostModal && (
            <div className="modal-overlay" onClick={() => setShowPostModal(false)}>
              <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>{selectedPost ? 'Edit' : 'Create'} Post</h2>
                  <button className="close-btn" onClick={() => setShowPostModal(false)}>×</button>
                </div>
                <form onSubmit={handleSavePost} className="form">
                  <div className="form-group">
                    <label>Post Title *</label>
                    <input
                      type="text"
                      value={postForm.post_title}
                      onChange={(e) => setPostForm({ ...postForm, post_title: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Post Content *</label>
                    <textarea
                      rows="6"
                      value={postForm.post_content}
                      onChange={(e) => setPostForm({ ...postForm, post_content: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Select Platforms *</label>
                    <div className="platforms-checkbox">
                      {platforms.map(platform => (
                        <label key={platform.id} className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={postForm.platforms.includes(platform.id)}
                            onChange={() => togglePlatform(platform.id, 'post')}
                          />
                          <span>{platform.icon} {platform.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Project</label>
                      <select
                        value={postForm.project_id}
                        onChange={(e) => setPostForm({ ...postForm, project_id: e.target.value })}
                      >
                        <option value="">Select Project</option>
                        {projects.map(project => (
                          <option key={project.id} value={project.id}>{project.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Campaign Phase</label>
                      <select
                        value={postForm.campaign_phase}
                        onChange={(e) => setPostForm({ ...postForm, campaign_phase: e.target.value })}
                      >
                        <option value="">Select Phase</option>
                        {campaignPhases.map(phase => (
                          <option key={phase} value={phase}>{phase}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Scheduled Date & Time</label>
                    <input
                      type="datetime-local"
                      value={postForm.scheduled_date}
                      onChange={(e) => setPostForm({ ...postForm, scheduled_date: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Media Files (Images/Videos)</label>
                    <input
                      type="file"
                      multiple
                      accept="image/*,video/*"
                      onChange={(e) => setMediaFiles(Array.from(e.target.files))}
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Hashtags</label>
                      <input
                        type="text"
                        value={postForm.hashtags}
                        onChange={(e) => setPostForm({ ...postForm, hashtags: e.target.value })}
                        placeholder="#realestate #mumbai #luxury"
                      />
                    </div>
                    <div className="form-group">
                      <label>Link URL</label>
                      <input
                        type="url"
                        value={postForm.link_url}
                        onChange={(e) => setPostForm({ ...postForm, link_url: e.target.value })}
                        placeholder="Enter URL (optional)"
                      />
                    </div>
                  </div>
                  <div className="form-actions">
                    <button type="button" className="btn-secondary" onClick={() => setShowPostModal(false)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn-primary">
                      {selectedPost ? 'Update' : 'Create'} Post
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Campaigns Tab */}
      {activeTab === 'campaigns' && (
        <div className="campaigns-content">
          <div className="section-header">
            <h2>Social Media Campaigns</h2>
            <button className="btn-primary" onClick={() => {
              setCampaignForm({
                campaign_name: '',
                project_id: '',
                campaign_type: 'general',
                start_date: '',
                end_date: '',
                target_platforms: [],
                target_audience: '',
                budget: '',
                description: ''
              });
              setShowCampaignModal(true);
            }}>
              + Create Campaign
            </button>
          </div>

          <div className="campaigns-grid">
            {campaigns.map(campaign => (
              <div key={campaign.id} className="campaign-card">
                <div className="campaign-header">
                  <h3>{campaign.campaign_name}</h3>
                  <span className={`status-badge ${campaign.status}`}>{campaign.status}</span>
                </div>
                <div className="campaign-meta">
                  <div><strong>Type:</strong> {campaign.campaign_type}</div>
                  <div><strong>Platforms:</strong> {campaign.target_platforms}</div>
                  {campaign.project_name && <div><strong>Project:</strong> {campaign.project_name}</div>}
                  {campaign.start_date && <div><strong>Start:</strong> {new Date(campaign.start_date).toLocaleDateString()}</div>}
                  {campaign.end_date && <div><strong>End:</strong> {new Date(campaign.end_date).toLocaleDateString()}</div>}
                  {campaign.posts_count > 0 && <div><strong>Posts:</strong> {campaign.posts_count}</div>}
                </div>
                {campaign.description && <p className="campaign-description">{campaign.description}</p>}
              </div>
            ))}
          </div>

          {showCampaignModal && (
            <div className="modal-overlay" onClick={() => setShowCampaignModal(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Create Campaign</h2>
                  <button className="close-btn" onClick={() => setShowCampaignModal(false)}>×</button>
                </div>
                <form onSubmit={handleSaveCampaign} className="form">
                  <div className="form-group">
                    <label>Campaign Name *</label>
                    <input
                      type="text"
                      value={campaignForm.campaign_name}
                      onChange={(e) => setCampaignForm({ ...campaignForm, campaign_name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Target Platforms *</label>
                    <div className="platforms-checkbox">
                      {platforms.map(platform => (
                        <label key={platform.id} className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={campaignForm.target_platforms.includes(platform.id)}
                            onChange={() => togglePlatform(platform.id, 'campaign')}
                          />
                          <span>{platform.icon} {platform.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Start Date</label>
                      <input
                        type="date"
                        value={campaignForm.start_date}
                        onChange={(e) => setCampaignForm({ ...campaignForm, start_date: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>End Date</label>
                      <input
                        type="date"
                        value={campaignForm.end_date}
                        onChange={(e) => setCampaignForm({ ...campaignForm, end_date: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      rows="4"
                      value={campaignForm.description}
                      onChange={(e) => setCampaignForm({ ...campaignForm, description: e.target.value })}
                    />
                  </div>
                  <div className="form-actions">
                    <button type="button" className="btn-secondary" onClick={() => setShowCampaignModal(false)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn-primary">
                      Create Campaign
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="analytics-content">
          <div className="section-header">
            <h2>Social Media Analytics</h2>
            <p>Track performance across all platforms</p>
          </div>
          {analytics.length === 0 ? (
            <div className="empty-state">
              <p>No analytics data available yet. Analytics will be populated as posts are published.</p>
            </div>
          ) : (
            <div className="analytics-table">
              <table>
                <thead>
                  <tr>
                    <th>Platform</th>
                    <th>Date</th>
                    <th>Posts</th>
                    <th>Reach</th>
                    <th>Engagement</th>
                    <th>Likes</th>
                    <th>Comments</th>
                    <th>Shares</th>
                    <th>Followers</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.map(stat => (
                    <tr key={stat.id}>
                      <td>{stat.platform}</td>
                      <td>{new Date(stat.date).toLocaleDateString()}</td>
                      <td>{stat.posts_count}</td>
                      <td>{stat.total_reach.toLocaleString()}</td>
                      <td>{stat.total_engagement.toLocaleString()}</td>
                      <td>{stat.total_likes.toLocaleString()}</td>
                      <td>{stat.total_comments.toLocaleString()}</td>
                      <td>{stat.total_shares.toLocaleString()}</td>
                      <td>{stat.followers_count.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SocialMedia;

